import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { QaiTableComponent, TableRow, HeaderConfig, QaiIconComponent } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { IAssignmentViewStatsResponse } from 'src/app/interfaces/reportingV2/iassignment-view-stats-response';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
  selector: 'app-simulation-sessions',
  templateUrl: './simulation-sessions.component.html',
  styleUrls: ['./simulation-sessions.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslocoModule, QaiTableComponent, DetailsSectionComponent, QaiIconComponent],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
})
export class SimulationSessionsComponent implements OnInit, OnChanges {
  @Input() totalSessionsCount: number = 0;
  @Input() sessionStats: IAssignmentViewStatsResponse[] = [];
  
  isTranslationLoaded = false;
  isLoading = false;
  currentPage = 1;
  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private localeService: LocaleService,
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2')
      .then(() => {
        this.isTranslationLoaded = true;
        this.initializeTable();
      })
      .catch((error) => {
        console.error('Error loading translations:', error);
        this.isTranslationLoaded = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sessionStats'] && this.isTranslationLoaded) {
      this.updateTableData();
    }
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate('USER_NAME', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('SESSION_DATE', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2'), 
        sortable: true
      }
    ];

    this.updateTableData();
  }

  private updateTableData(): void {
    if (this.sessionStats && this.sessionStats.length > 0) {
      this.tableData = this.transformSessionStatsToTableRows(this.sessionStats);
    } else {
      this.tableData = [];
    }
  }

  private transformSessionStatsToTableRows(stats: IAssignmentViewStatsResponse[]): TableRow[] {
    return stats.map(stat => {
      const safeValue = (val: any, formatter?: (v: any) => string): string => {
        if (val === null || val === undefined) {
          return '—';
        }
        return formatter ? formatter(val) : val.toString();
      };

      const row: any = {
        id: stat.uuid,
        [this.translocoService.translate('USER_NAME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(stat.userName)
        },
        [this.translocoService.translate('SESSION_DATE', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(stat.sessionDate, v => this.localeService.formatSessionDateTime(v)),
          ...(stat.sessionDate ? { sortValue: stat.sessionDate } : {})
        },
        [this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(stat.durationSeconds, v => this.localeService.formatDurationInSecondsWithLabel(v, true))
        },
      [this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.overallScore, v => this.localeService.formatScoreValue(v))
      },
      [this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.messagingScore, v => this.localeService.formatScoreValue(v))
      },
      [this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.methodologyScore, v => this.localeService.formatScoreValue(v))
      },
      [this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.skillsScore, v => this.localeService.formatScoreValue(v))
      }
      };
      return row as TableRow;
    });
  }

  onPageChanged(event: any): void {
    this.currentPage = event.page;
  }

  onDownloadClick(): void {
    console.log('Download clicked for simulation sessions');
  }
}

